/**
 * Tooltip — apparaît après 150 ms de survol, disparaît instantanément.
 * Usage :
 *   <Tooltip content="Blacklister ce candidat">
 *     <button ...><Ban /></button>
 *   </Tooltip>
 */
import { useState, useRef, ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Position = 'top' | 'bottom' | 'left' | 'right'

interface TooltipProps {
  content: string
  children: ReactNode
  position?: Position
  className?: string
}

const POSITION_CLASSES: Record<Position, string> = {
  top:    'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left:   'right-full top-1/2 -translate-y-1/2 mr-2',
  right:  'left-full top-1/2 -translate-y-1/2 ml-2',
}

export default function Tooltip({
  content,
  children,
  position = 'top',
  className,
}: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const show = () => {
    timerRef.current = setTimeout(() => setVisible(true), 150)
  }

  const hide = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setVisible(false)
  }

  return (
    <div
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible && (
        <span
          role="tooltip"
          className={cn(
            'absolute z-[9999] px-2 py-1 text-xs font-medium text-white',
            'bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg',
            'whitespace-nowrap pointer-events-none',
            POSITION_CLASSES[position],
          )}
          style={{ animation: 'tooltipFadeIn 80ms ease forwards' }}
        >
          {content}
        </span>
      )}
    </div>
  )
}
